import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface Invitation {
  id: string
  token_display: string  // first 8 chars of raw token, stored temporarily in state only
  invited_email: string | null
  status: 'pending' | 'accepted' | 'revoked' | 'expired'
  expires_at: string
  accepted_at: string | null
  created_at: string
}

interface InvitationRow {
  id: string
  invited_email: string | null
  status: string
  expires_at: string
  accepted_at: string | null
  created_at: string
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
}

function isExpired(iso: string) {
  return new Date(iso) < new Date()
}

const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  pending:  { bg: 'rgba(212,151,43,0.12)', color: '#9A6C0E', label: 'Pending' },
  accepted: { bg: 'rgba(15,124,108,0.1)',  color: '#0F7C6C', label: 'Accepted' },
  revoked:  { bg: 'rgba(224,102,59,0.1)',  color: '#C0522A', label: 'Revoked' },
  expired:  { bg: 'rgba(27,42,74,0.07)',   color: 'rgba(27,42,74,0.4)', label: 'Expired' },
}

export default function Enroll() {
  const { user, profile } = useAuth()

  const [invitations,  setInvitations]  = useState<InvitationRow[]>([])
  const [loadingList,  setLoadingList]  = useState(true)

  // New invite form
  const [email,        setEmail]        = useState('')
  const [generating,   setGenerating]   = useState(false)
  const [newCode,      setNewCode]      = useState<string | null>(null)
  const [copied,       setCopied]       = useState(false)
  const [genError,     setGenError]     = useState('')

  const [revoking,     setRevoking]     = useState<string | null>(null)

  const fetchInvitations = useCallback(async () => {
    if (!user) return
    setLoadingList(true)
    const { data } = await supabase
      .from('invitations')
      .select('id, invited_email, status, expires_at, accepted_at, created_at')
      .eq('instructor_id', user.id)
      .order('created_at', { ascending: false })
    setInvitations((data as InvitationRow[]) ?? [])
    setLoadingList(false)
  }, [user])

  useEffect(() => { fetchInvitations() }, [fetchInvitations])

  const handleGenerate = async () => {
    if (generating || !user) return
    setGenError(''); setNewCode(null); setCopied(false)
    if (!email.trim()) { setGenError('Student email is required.'); return }
    setGenerating(true)
    try {
      const { data, error } = await supabase.functions.invoke('send-invite', {
        body: { studentEmail: email.trim(), instructorName: profile?.full_name ?? '' },
      })

      if (error) { setGenError('Could not send invite. Please try again.'); return }
      if (data?.error) { setGenError(data.error); return }
      if (data?.warning) { setGenError(data.warning) }

      setNewCode(data.token)
      setEmail('')
      fetchInvitations()
    } finally {
      setGenerating(false)
    }
  }

  const handleCopy = () => {
    if (!newCode) return
    navigator.clipboard.writeText(newCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleRevoke = async (id: string) => {
    setRevoking(id)
    await supabase.from('invitations').update({ status: 'revoked' }).eq('id', id)
    setRevoking(null)
    fetchInvitations()
  }

  const effectiveStatus = (inv: InvitationRow): string => {
    if (inv.status === 'pending' && isExpired(inv.expires_at)) return 'expired'
    return inv.status
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>Enroll a student</div>
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 28 }}>
        Generate an invite code and share it with your student. It's valid for 7 days.
      </div>

      {/* Generate form */}
      <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '22px 22px', marginBottom: 24 }}>
        <div style={{ font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 14 }}>
          New invite code
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', display: 'block', marginBottom: 6 }}>
              Student email
            </label>
            <input
              type='email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder='student@example.com'
              className='field-input'
              required
            />
          </div>
        </div>

        {genError && <div className='banner-error' style={{ marginBottom: 12 }}>{genError}</div>}

        <button className='btn-teal' onClick={handleGenerate} disabled={generating} style={{ width: 'auto', padding: '11px 22px' }}>
          {generating ? 'Generating…' : 'Generate invite code'}
        </button>

        {/* Show new code */}
        {newCode && (
          <div style={{ marginTop: 18, padding: '16px 18px', background: 'rgba(15,124,108,0.07)', border: '1px solid rgba(15,124,108,0.2)', borderRadius: 11 }}>
            <div style={{ font: '600 12px Inter, sans-serif', color: '#0F7C6C', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Share this code with your student
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                flex: 1, font: '600 15px/1 "Courier New", monospace', color: '#1B2A4A',
                background: '#fff', border: '1px solid rgba(27,42,74,0.12)', borderRadius: 8,
                padding: '11px 14px', wordBreak: 'break-all',
              }}>
                {newCode}
              </div>
              <button
                onClick={handleCopy}
                style={{
                  padding: '11px 16px', background: copied ? '#0F7C6C' : '#1B2A4A',
                  border: 'none', borderRadius: 8, font: '600 13px Inter, sans-serif',
                  color: '#fff', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s',
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 10 }}>
              Expires in 7 days. The student enters this on the registration page.
            </div>
          </div>
        )}
      </div>

      {/* Invitations list */}
      <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '16px 22px', borderBottom: '1px solid rgba(27,42,74,0.06)', font: '600 15px Inter, sans-serif', color: '#1B2A4A' }}>
          Sent invites
        </div>

        {loadingList ? (
          <div style={{ padding: '24px 22px', font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading…</div>
        ) : invitations.length === 0 ? (
          <div style={{ padding: '24px 22px', font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>
            No invites yet. Generate one above.
          </div>
        ) : (
          invitations.map(inv => {
            const status = effectiveStatus(inv)
            const s = STATUS_STYLE[status] ?? STATUS_STYLE.pending
            return (
              <div key={inv.id} style={{
                padding: '14px 22px', borderBottom: '1px solid rgba(27,42,74,0.05)',
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: '500 13.5px Inter, sans-serif', color: '#1B2A4A' }}>
                    {inv.invited_email ?? '—'}
                  </div>
                  <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 2 }}>
                    Created {formatDate(inv.created_at)}
                    {inv.accepted_at ? ` · Accepted ${formatDate(inv.accepted_at)}` : ` · Expires ${formatDate(inv.expires_at)}`}
                  </div>
                </div>

                <div style={{
                  padding: '4px 10px', borderRadius: 20, background: s.bg,
                  font: '600 11.5px Inter, sans-serif', color: s.color, flexShrink: 0,
                }}>
                  {s.label}
                </div>

                {status === 'pending' && (
                  <button
                    onClick={() => handleRevoke(inv.id)}
                    disabled={revoking === inv.id}
                    style={{
                      padding: '5px 12px', background: 'transparent',
                      border: '1px solid rgba(224,102,59,0.3)', borderRadius: 7,
                      font: '500 12px Inter, sans-serif', color: '#C0522A',
                      cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    {revoking === inv.id ? '…' : 'Revoke'}
                  </button>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
